import { z } from 'zod'

import { auditShape } from '@/shared/details'

export * from './paths'

/** The units a course's duration is given in. */
export const durationUnits = ['minutes', 'hours', 'days', 'weeks'] as const

export type DurationUnit = (typeof durationUnits)[number]

/** The colors a course's classes show in on calendars, as legacy names them. */
export const calendarColors = [
  'blue',
  'darkBlue',
  'gray',
  'darkGray',
  'green',
  'darkGreen',
  'maroon',
  'darkMaroon',
  'olive',
  'darkOlive',
  'orange',
  'darkOrange',
  'peach',
  'darkPeach',
  'purple',
  'darkPurple',
  'red',
  'darkRed',
  'steel',
  'darkSteel',
  'teal',
  'darkTeal',
  'yellow',
  'darkYellow',
] as const

export type CalendarColor = (typeof calendarColors)[number]

const courseSchema = z
  .object({
    id: z.string(),
    organizationId: z.string(),
    organizationName: z.string(),
    name: z.string(),
    shortName: z.string().nullish(),
    courseVersion: z.string().nullish(),
    keywords: z.string().nullish(),
    description: z.string().nullish(),
    htmlDescription: z.string().nullish(),
    imageUrl: z.string().nullish(),
    duration: z.number(),
    durationUnit: z.enum(durationUnits),
    trainingDays: z.number(),
    availableInstructorLed: z.boolean(),
    availableSelfPaced: z.boolean(),
    showsOnCatalog: z.boolean(),
    hasSimpleFeedback: z.boolean().optional(),
    isFeatured: z.boolean(),
    featuredUntil: z.string().nullish(),
    allowsAnonymousDetails: z.boolean(),
    isTemplate: z.boolean().optional(),
    defaultClassCapacity: z.number().nullish(),
    absoluteClassCapacity: z.number().nullish(),
    overridesOrganizationCapacity: z.boolean(),
    postClassLabHours: z.number(),
    postClassScormHours: z.number(),
    allowsActivityDisablement: z.boolean(),
    autoEnablesActivitiesAfterClass: z.boolean(),
    allowsClassSelfEnrollment: z.boolean(),
    calendarColor: z.enum(calendarColors),
    externalId: z.string().nullish(),
    isArchived: z.boolean(),
    autoArchiveOn: z.string().nullish(),
    madeAvailableOn: z.string().nullish(),
    contentProviderId: z.string().nullish(),
    contentProviderName: z.string().nullish(),
    isContentProviderDeleted: z.boolean().optional(),
    studentSurvey: z.object({ id: z.string(), name: z.string() }).nullish(),
    instructorSurvey: z.object({ id: z.string(), name: z.string() }).nullish(),
    organizationSurvey: z.object({ id: z.string(), name: z.string() }).nullish(),
    isMicrosoftLearn: z.boolean().optional(),
    microsoftLearnUrl: z.string().nullish(),
  })
  .extend(auditShape)

/** A course as Course Details and Edit Course show it. */
export type Course = z.infer<typeof courseSchema>

export function parseCourse(body: unknown): Course {
  return courseSchema.parse(body)
}

const defaultsSchema = z.object({
  availableInstructorLed: z.boolean(),
  availableSelfPaced: z.boolean(),
  showsOnCatalog: z.boolean(),
  hasSimpleFeedback: z.boolean().optional(),
  duration: z.number(),
  durationUnit: z.enum(durationUnits),
  trainingDays: z.number(),
  postClassLabHours: z.number(),
  postClassScormHours: z.number(),
  calendarColor: z.enum(calendarColors),
})

/** What a new course in an organization starts with. */
export type CourseDefaults = z.infer<typeof defaultsSchema>

export function parseCourseDefaults(body: unknown): CourseDefaults {
  return defaultsSchema.parse(body)
}
